import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Download, Check, Loader2, Share, Plus, X } from 'lucide-react';

export default function DownloadAppButton({ variant = 'default', size = 'default', className = '' }) {
  const [isInstalled, setIsInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS/iPadOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);

    // Listen for the beforeinstallprompt event (Chrome/Edge only)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      window.deferredInstallPrompt = e;
      console.log('[PWA] Install prompt available');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for app installed
    const handleInstalled = () => {
      console.log('[PWA] App installed successfully');
      setIsInstalled(true);
      window.deferredInstallPrompt = null;
    };
    
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    // For iOS - show the visual guide
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    // For Chrome/Edge - use native prompt
    const prompt = window.deferredInstallPrompt;
    
    if (prompt) {
      setInstalling(true);
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        
        if (outcome === 'accepted') {
          setIsInstalled(true);
        }
        
        window.deferredInstallPrompt = null;
      } catch (err) {
        console.error('[PWA] Install error:', err);
      } finally {
        setInstalling(false);
      }
    } else {
      // Fallback for other browsers
      alert('To install: Look for the install icon in your browser\'s address bar or menu');
    }
  };

  if (isInstalled) {
    return (
      <Button 
        variant="outline" 
        size={size}
        className={`text-green-600 border-green-600 hover:bg-green-50 cursor-default ${className}`}
        disabled
      >
        <Check className="w-4 h-4 mr-2" />
        Installed
      </Button>
    );
  }

  return (
    <>
      <Button 
        onClick={handleInstall}
        variant={variant}
        size={size}
        className={className}
        disabled={installing}
        data-testid="download-app-btn"
      >
        {installing ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Download className="w-4 h-4 mr-2" />
        )}
        {installing ? 'Installing...' : 'Download App'}
      </Button>

      {/* iOS Add to Home Screen Guide - rendered via portal to escape stacking contexts */}
      {showIOSGuide && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center md:items-center justify-center animate-in fade-in duration-200" onClick={() => setShowIOSGuide(false)}>
          <div className="bg-white w-full max-w-lg mx-4 rounded-2xl p-6 animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Close button */}
            <button 
              onClick={() => setShowIOSGuide(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 z-10"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-700 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900">Install 1Clin</h3>
              <p className="text-slate-500 text-sm mt-1">Add to your home screen for quick access</p>
            </div>

            {/* Steps */}
            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center shrink-0">
                  <Share className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Step 1: Tap Share</div>
                  <div className="text-sm text-slate-500">Tap the share icon at the bottom of Safari</div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>

              {/* Step 2 */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center shrink-0">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Step 2: Add to Home Screen</div>
                  <div className="text-sm text-slate-500">Scroll down and tap "Add to Home Screen"</div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>

              {/* Step 3 */}
              <div className="flex items-center gap-4 p-4 bg-teal-50 rounded-xl border-2 border-teal-200">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Step 3: Tap "Add"</div>
                  <div className="text-sm text-slate-500">Confirm by tapping Add in the top right</div>
                </div>
              </div>
            </div>

            {/* Pointer to Safari share button */}
            <div className="mt-6 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-blue-600 font-medium animate-bounce">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                Look for the Share button below
              </div>
            </div>

            {/* Got it button */}
            <Button 
              onClick={() => setShowIOSGuide(false)}
              className="w-full mt-4 bg-teal-600 hover:bg-teal-700"
            >
              Got it
            </Button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
